import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import propertiesApi from "@/api/propertiesApi";
import PropertyForm from "@/components/PropertyForm/PropertyForm";
import ApiError from "@/model/ApiError";
import PropertyFormValues from "@/model/PropertyFormValues";
import propertyListPath from "@/routing/propertyListPath";
import propertyForm from "@/utils/propertyForm";
import "@/pages/CreatePropertyPage/CreatePropertyPage.scss";

/**
 * Admins reach this page from a company (/companies/:id/properties/new) and create the listing for
 * that company; hosts use /properties/new and the backend attaches their own company.
 */
function CreatePropertyPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id: companyId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!session) {
    return null;
  }

  const handleSubmit = async (values: PropertyFormValues) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const created = await propertiesApi.createProperty(
        session.accessToken,
        propertyForm.toRequest(values, { companyId })
      );
      navigate(`/properties/${created.id}/edit`, {
        replace: true,
        state: { justCreated: true },
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("properties.form.validationError"));
      } else if (err instanceof ApiError && err.status === 403) {
        setError(t("properties.form.forbiddenError"));
      } else {
        setError(t("properties.form.genericError"));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-property-page">
      <PropertyForm
        title={t("properties.create.title")}
        initialValues={propertyForm.emptyValues()}
        submitLabel={t("properties.create.submit")}
        isSubmitting={isSubmitting}
        error={error}
        cancelTo={propertyListPath(session.role, companyId)}
        onSubmit={handleSubmit}
      >
        <p className="create-property-page__hint">{t("properties.create.photosHint")}</p>
      </PropertyForm>
    </div>
  );
}

export default CreatePropertyPage;
